import type { ProcessoArbitralStatus } from '@/features/arbitral';
import { buscarBytesRemotos, salvarBlobComoArquivo } from '@/shared/utils/browserActions';
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  downloadBlob,
  request,
} from '@/shared/utils/requestBuilder';
import { assertUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import type {
  AndamentoProcesso,
  CancelarProcessoResultado,
  CreateParteProcessoPayload,
  CriarSolicitacaoPayload,
  DocumentoProcesso,
  MensagemChatItem,
  ProcessoArbitralDetail,
  SituacaoAtiva,
  SolicitacaoAlteracao,
} from '../types/arbitralDetailTypes';
import type {
  CobrancaProcessoClienteApi,
  FinanceiroProcessoCliente,
  ItemConsumoCliente,
  PaginaCliente,
} from '../types/financeiroProcessoClienteTypes';
import {
  normalizeAndamento,
  normalizeMensagem,
  normalizeProcesso,
  normalizeSituacao,
  parseErrorMessage,
} from '../utils/arbitralDetailNormalizers';
import type {
  DocumentoPadraoJuntado,
  JuntarDocumentoPadraoPayload,
} from '../types/juntadaDocumentoPadraoTypes';
import { montarMultipartSolicitacao } from '../utils/solicitacaoMultipart';
import { lerDadosDaRespostaDoCore } from '@/shared/utils/respostaDoCore';

/**
 * Sem teto, uma conexão que trava deixa o painel preso em "confirmando", com Cancelar e Fechar
 * desabilitados (SEC-01 do review do #1358). O estouro cai como falha sem resposta: o hook mantém a
 * chave, e a nova tentativa devolve a juntada que o core já tiver feito. Mais folga que as leituras
 * porque a juntada trava o processo e pode esperar outra confirmação no mesmo processo.
 */
const TIMEOUT_DA_JUNTADA_MS = 30_000;

async function parseSituacoesResponse(response: Response): Promise<SituacaoAtiva[]> {
  const body = (await response.json()) as { items: Record<string, unknown>[] };
  return (body.items ?? []).map(normalizeSituacao);
}

export interface PatchParteBody {
  email: string;
  telefone: string;
}

async function postPendencia(
  processoId: string,
  pendenciaId: string,
  acao: 'resolver' | 'reenviar',
  resposta: string,
  arquivos?: File[],
): Promise<void> {
  const form = new FormData();
  form.append('resposta', resposta);
  if (arquivos && arquivos.length > 0) {
    arquivos.forEach((f) => form.append('arquivos', f));
  }
  const response = await apiPost(
    `/arbitral/processos/${processoId}/pendencias/${pendenciaId}/${acao}`,
    form,
  );
  if (!response.ok) throw new Error(`Erro ao ${acao} pendência`);
}

/**
 * Dois saltos de propósito: o core assina (chamada com cookie) e o browser busca os bytes DIRETO
 * do bucket. O `/preview` fazia o core bufferizar o arquivo inteiro na resposta, o que estoura o
 * teto de 32 MiB de resposta não-streamada do Cloud Run e derrubava com 500 todo documento grande
 * (decisão D12; mesmo par que a secretaria usa desde a etapa 3b, em `secretaria-arbitral`).
 *
 * A URL é credencial de vida curta (`GCS_SIGNED_URL_TTL`, 300s, e o core responde `no-store`):
 * peça imediatamente antes de usar, nunca guarde em estado de lista.
 */
async function urlAssinadaDoDocumento(processoId: string, docId: string): Promise<string> {
  const response = await apiGet(`/arbitral/processos/${processoId}/documentos/${docId}/download`);
  if (!response.ok) throw new Error('Erro ao gerar o link do documento');
  const body = (await response.json()) as { data?: { url?: unknown } };
  return assertUrlNavegavelSegura(body?.data?.url, 'Link do documento');
}

async function bytesDoDocumento(processoId: string, docId: string): Promise<Blob> {
  return buscarBytesRemotos(await urlAssinadaDoDocumento(processoId, docId));
}

export const arbitralDetalhesService = {
  exportarHistoricoAndamentos: async (
    processoId: string,
    numeroProcesso: string,
    signal?: AbortSignal,
  ): Promise<void> => {
    await downloadBlob(
      `/arbitral/processos/${processoId}/andamentos/exportacao`,
      `historico-andamentos-${numeroProcesso}.xlsx`,
      signal,
    );
  },

  fetchProcesso: async (processoId: string): Promise<ProcessoArbitralDetail> => {
    const response = await apiGet(`/arbitral/processos/${processoId}`);
    if (!response.ok) throw new Error('Erro ao carregar processo');
    const body = await response.json();
    return normalizeProcesso(body.data as ProcessoArbitralDetail);
  },

  /** Resumo agregado NO BANCO — união discriminada por modalidade. */
  fetchResumoFinanceiro: async (processoId: string): Promise<FinanceiroProcessoCliente> => {
    const response = await apiGet(`/arbitral/processos/${processoId}/financeiro/resumo`);
    if (!response.ok) throw new Error('Erro ao carregar financeiro do processo');
    const body = await response.json();
    return body.data as FinanceiroProcessoCliente;
  },

  /** Cobranças do processo (paginado). Vazio no convênio, que não tem cobrança. */
  fetchCobrancas: async (
    processoId: string,
    page = 1,
  ): Promise<PaginaCliente<CobrancaProcessoClienteApi>> => {
    const response = await apiGet(
      `/arbitral/processos/${processoId}/financeiro/cobrancas?page=${page}&limit=50`,
    );
    if (!response.ok) throw new Error('Erro ao carregar cobranças do processo');
    const body = await response.json();
    return body.data as PaginaCliente<CobrancaProcessoClienteApi>;
  },

  /** Consumo do ciclo item a item — um por AR. A custa viaja no resumo. */
  fetchExtratoConsumo: async (
    processoId: string,
    page = 1,
  ): Promise<PaginaCliente<ItemConsumoCliente>> => {
    const response = await apiGet(
      `/arbitral/processos/${processoId}/financeiro/extrato?page=${page}&limit=50`,
    );
    if (!response.ok) throw new Error('Erro ao carregar o consumo do ciclo');
    const body = await response.json();
    return body.data as PaginaCliente<ItemConsumoCliente>;
  },

  /**
   * Reenvia o link de pagamento da cobrança para o e-mail do usuário autenticado.
   *
   * O corpo vai VAZIO de propósito: o destinatário é resolvido no servidor a
   * partir do token (`Usuario.email`), nunca escolhido pelo cliente — mandar um
   * endereço aqui devolve 400.
   */
  enviarSegundaVia: async (processoId: string, cobrancaId: string): Promise<void> => {
    const response = await apiPost(
      `/arbitral/processos/${processoId}/financeiro/cobrancas/${cobrancaId}/segunda-via`,
      {},
    );
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Não conseguimos reenviar a 2ª via agora'));
    }
  },
  /**
   * Comprovante da transação no gateway. POST porque é read-through: a primeira
   * leitura busca no Asaas e persiste — esconder isso atrás de um GET convidaria
   * cache e prefetch a gastar requisição sem ninguém ter pedido o comprovante.
   */
  obterRecibo: async (processoId: string, cobrancaId: string): Promise<string> => {
    const response = await apiPost(
      `/arbitral/processos/${processoId}/financeiro/cobrancas/${cobrancaId}/recibo`,
      {},
    );
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Comprovante indisponível no momento'));
    }
    const body = await response.json();
    return (body.data as { reciboUrl: string }).reciboUrl;
  },
  /** Cancela a custa em atraso e retoma o nó de cobrança. Motivo obrigatório (10+).*/
  regerarCobranca: async (
    processoId: string,
    cobrancaId: string,
    motivo: string,
  ): Promise<void> => {
    const response = await apiPost(
      `/arbitral/processos/${processoId}/financeiro/cobrancas/${cobrancaId}/regerar`,
      { motivo },
    );
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Não conseguimos gerar a nova cobrança'));
    }
  },

  reabrirProcesso: async (
    processoId: string,
    body: { novoStatus: ProcessoArbitralStatus; motivo: string },
  ): Promise<void> => {
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/reabrir`, body);
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Erro ao reabrir o processo'));
    }
  },

  patchParte: async (processoId: string, parteId: string, data: PatchParteBody): Promise<void> => {
    const response = await apiPatch(`/arbitral/processos/${processoId}/partes/${parteId}`, data);
    if (!response.ok) throw new Error('Erro ao atualizar contato');
  },

  createParte: async (processoId: string, data: CreateParteProcessoPayload): Promise<void> => {
    const response = await apiPost(`/arbitral/processos/${processoId}/partes`, data);
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(
        (errorBody as { error?: { message?: string } }).error?.message ?? 'Erro ao adicionar parte',
      );
    }
  },

  reenviarNotificacao: async (processoId: string, parteId: string): Promise<void> => {
    const response = await apiPost(
      `/arbitral/processos/${processoId}/partes/${parteId}/notificacao/reenviar`,
    );
    if (!response.ok) throw new Error('Erro ao reenviar notificação');
  },

  resolverPendencia: async (
    processoId: string,
    pendenciaId: string,
    resposta: string,
    arquivos?: File[],
  ): Promise<void> => postPendencia(processoId, pendenciaId, 'resolver', resposta, arquivos),

  reenviarPendencia: async (
    processoId: string,
    pendenciaId: string,
    resposta: string,
    arquivos?: File[],
  ): Promise<void> => postPendencia(processoId, pendenciaId, 'reenviar', resposta, arquivos),

  listarSituacoesDisponiveis: async (): Promise<SituacaoAtiva[]> => {
    const response = await apiGet('/admin/arbitral/situacoes', { active: true });
    if (!response.ok) throw new Error('Erro ao carregar situações');
    return parseSituacoesResponse(response);
  },

  vincularSituacoes: async (
    processoId: string,
    situacaoIds: string[],
  ): Promise<SituacaoAtiva[]> => {
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/situacoes`, {
      situacaoIds,
    });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Erro ao vincular situação'));
    }
    return parseSituacoesResponse(response);
  },

  desvincularSituacao: async (processoId: string, situacaoId: string): Promise<SituacaoAtiva[]> => {
    const response = await apiDelete(
      `/admin/arbitral/processos/${processoId}/situacoes/${situacaoId}`,
    );
    if (!response.ok) throw new Error('Erro ao remover situação');
    return parseSituacoesResponse(response);
  },

  listarDocumentos: async (processoId: string): Promise<DocumentoProcesso[]> => {
    const response = await apiGet(`/arbitral/processos/${processoId}/documentos`);
    if (!response.ok) throw new Error('Erro ao listar documentos');
    const body = await response.json();
    return body.data as DocumentoProcesso[];
  },

  // Salva o blob em vez de navegar até a signed URL: o atributo `download` do <a> é ignorado em
  // URL cross-origin, então navegar até o bucket perderia o nome original do arquivo.
  baixarDocumento: async (
    processoId: string,
    docId: string,
    nomeOriginal: string,
  ): Promise<void> => {
    salvarBlobComoArquivo(await bytesDoDocumento(processoId, docId), nomeOriginal);
  },

  // Object URL same-origin, e não a signed URL direto no visor: ela viaja com
  // `response-content-disposition: attachment` DENTRO da assinatura (`buildContentDisposition`
  // no core não tem ramo `inline`), então apontar um `<iframe>`/`<img>` para ela baixaria o
  // arquivo em vez de renderizá-lo. Quem revoga o object URL é o visor, como antes.
  previewDocumento: async (processoId: string, docId: string): Promise<string> => {
    return window.URL.createObjectURL(await bytesDoDocumento(processoId, docId));
  },

  /**
   * Junta aos autos uma versão da biblioteca: o core cria a cópia, o andamento e o uso numa
   * transação só. Sem a recuperação de workspace no 403: ela refaria o POST sem o header e, se o
   * 403 persistisse, recarregaria a página — o painel precisa receber o "sem permissão" como veio.
   */
  juntarDocumentoPadrao: async (
    processoId: string,
    payload: JuntarDocumentoPadraoPayload,
  ): Promise<DocumentoPadraoJuntado> =>
    lerDadosDaRespostaDoCore<DocumentoPadraoJuntado>(
      await request({
        endpoint: `/arbitral/processos/${processoId}/documentos-padrao`,
        method: 'POST',
        body: payload,
        recuperarWorkspaceNo403: false,
        signal: AbortSignal.timeout(TIMEOUT_DA_JUNTADA_MS),
      }),
      'Não foi possível usar o documento padrão no processo',
    ),

  removerDocumento: async (processoId: string, docId: string): Promise<void> => {
    const response = await apiDelete(`/arbitral/processos/${processoId}/documentos/${docId}`);
    if (!response.ok) throw new Error('Erro ao remover documento');
  },

  listarAndamentos: async (processoId: string): Promise<AndamentoProcesso[]> => {
    const response = await apiGet(`/arbitral/processos/${processoId}/andamentos`);
    if (!response.ok) throw new Error('Erro ao listar andamentos');
    const body = await response.json();
    return (body.data as Record<string, unknown>[]).map(normalizeAndamento);
  },

  criarAndamento: async (
    processoId: string,
    dados: {
      descricao: string;
      arquivos: File[];
    },
  ): Promise<AndamentoProcesso> => {
    const form = new FormData();
    form.append('descricao', dados.descricao);
    dados.arquivos.forEach((f) => form.append('anexos', f));
    const response = await apiPost(`/arbitral/processos/${processoId}/andamentos`, form);
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(
        (errorBody as { error?: { message?: string } }).error?.message ??
          'Erro ao registrar andamento',
      );
    }
    const body = await response.json();
    return normalizeAndamento(body.data as Record<string, unknown>);
  },

  listarMensagensChat: async (
    processoId: string,
    page = 1,
    limit = 50,
  ): Promise<{ mensagens: MensagemChatItem[]; total: number }> => {
    const response = await apiGet(`/arbitral/processos/${processoId}/chat`, { page, limit });
    if (!response.ok) throw new Error('Erro ao carregar mensagens');
    const body = await response.json();
    const data = body.data as Record<string, unknown>;
    return {
      mensagens: (data.mensagens as Record<string, unknown>[]).map(normalizeMensagem),
      total: Number(data.total ?? 0),
    };
  },

  enviarMensagemChat: async (
    processoId: string,
    texto: string,
    arquivos: File[],
  ): Promise<MensagemChatItem> => {
    const form = new FormData();
    if (texto.trim()) form.append('texto', texto.trim());
    arquivos.forEach((f) => form.append('arquivos', f));
    const response = await apiPost(`/arbitral/processos/${processoId}/chat`, form);
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(
        (errorBody as { error?: { message?: string } }).error?.message ?? 'Erro ao enviar mensagem',
      );
    }
    const body = await response.json();
    return normalizeMensagem(body.data as Record<string, unknown>);
  },

  marcarMensagemLida: async (processoId: string, msgId: string): Promise<void> => {
    const response = await apiPatch(`/arbitral/processos/${processoId}/chat/${msgId}/lida`, {});
    if (!response.ok) throw new Error('Erro ao marcar mensagem como lida');
  },

  contarNaoLidasChat: async (processoId: string): Promise<number> => {
    const response = await apiGet(`/arbitral/processos/${processoId}/chat/nao-lidas`);
    if (!response.ok) throw new Error('Erro ao contar não lidas');
    const body = await response.json();
    return Number((body.data as Record<string, unknown>).total ?? 0);
  },

  enviarTypingEvent: async (processoId: string): Promise<void> => {
    await apiPost(`/arbitral/processos/${processoId}/chat/typing`, {}).catch(() => {});
  },

  listarSolicitacoes: async (processoId: string): Promise<SolicitacaoAlteracao[]> => {
    const response = await apiGet(`/arbitral/processos/${processoId}/solicitacoes`);
    if (!response.ok) throw new Error('Erro ao carregar solicitações');
    const body = await response.json();
    return (body.data?.items ?? []) as SolicitacaoAlteracao[];
  },

  criarSolicitacao: async (
    processoId: string,
    payload: CriarSolicitacaoPayload,
  ): Promise<SolicitacaoAlteracao> => {
    const { anexos, ...corpo } = payload;
    const body = anexos && anexos.length > 0 ? montarMultipartSolicitacao(corpo, anexos) : corpo;
    const response = await apiPost(`/arbitral/processos/${processoId}/solicitacoes`, body);
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Erro ao enviar solicitação'));
    }
    const responseBody = await response.json();
    return responseBody.data as SolicitacaoAlteracao;
  },

  cancelarSolicitacao: async (
    processoId: string,
    solicitacaoId: string,
  ): Promise<SolicitacaoAlteracao> => {
    const response = await apiPost(
      `/arbitral/processos/${processoId}/solicitacoes/${solicitacaoId}/cancelar`,
    );
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Erro ao cancelar solicitação'));
    }
    const body = await response.json();
    return body.data as SolicitacaoAlteracao;
  },

  cancelarProcesso: async (processoId: string): Promise<CancelarProcessoResultado> => {
    const response = await apiPost(`/arbitral/processos/${processoId}/cancelar`);
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Erro ao cancelar processo'));
    }
    const body = await response.json();
    return body.data as CancelarProcessoResultado;
  },
};
